/**
 * Font-family context — lets consumers supply a weight→fontFamily mapping
 * without coupling a3s-ui to a specific font library.
 *
 * On React Native, fontFamily does not cascade from parent components. The
 * Text component reads from this context to resolve the correct fontFamily
 * for a given fontWeight so that extracted components (Button, Banner, etc.)
 * render with the app's custom fonts on native.
 *
 * On web, CSS font-family cascades normally, so this context is optional.
 */
import { createContext, useContext, type FC, type ReactNode } from "react";
import type { TextStyle } from "react-native";

/**
 * Maps a numeric fontWeight string ("400", "500", "600", "700") to the
 * platform fontFamily name registered via expo-font / Asset linking.
 *
 * Return `undefined` for weights that should use the system default.
 */
export type FontFamilyResolver = (weight: NonNullable<TextStyle["fontWeight"]>) => string | undefined;

const FontFamilyContext = createContext<FontFamilyResolver | undefined>(undefined);

export interface FontFamilyProviderProps {
  readonly resolve: FontFamilyResolver;
  readonly children: ReactNode;
}

export const FontFamilyProvider: FC<FontFamilyProviderProps> = ({ resolve, children }) => (
  <FontFamilyContext.Provider value={resolve}>{children}</FontFamilyContext.Provider>
);

export function useFontFamilyResolver(): FontFamilyResolver | undefined {
  return useContext(FontFamilyContext);
}
